import type { TFunction } from 'i18next';
import type {
  MediaHydrated,
  Tag,
  Variant,
} from '../../lib/flotiq-client/types/api-response';
import type { DataGridColumnOptionsProps } from '../DataGridControl/DataGridControl';

import { twMerge } from 'tailwind-merge';

import { getMediaUrl } from '../../lib/flotiq-client/api-helpers';
import {
  getDataSize,
  getDefaultImageProperties,
  getTestProps,
  isImagePreviewSupported,
} from '../../lib/helpers';

import { AddVariantIcon, ArrowUpRightIcon, TagIcon } from '../../images/shapes';

import Button from '../Button/Button';
import { defaultRenderer } from '../DataGrid/DataGridCell/cellRenderer';
import DateContainer from '../DateContainer/DateContainer';
import StatusPill from '../StatusPill/StatusPill';

export type TagEditFn = (media: MediaHydrated) => void;
export type TagClickFn = (id: string, name: string) => void;

export const getDefinedColumns = (
  gridOptionsByID: Record<string, DataGridColumnOptionsProps>,
  onTagEdit: TagEditFn,
  onTagClick: TagClickFn,
  onVariantCreate: (media: MediaHydrated) => Promise<void>,
  canUpdate: boolean,
  t: TFunction,
  testId = '',
) => ({
  preview: {
    accessor: 'preview',
    label: t('Media.Preview'),
    width: gridOptionsByID?.['preview']?.width,
    render: (_: undefined, mediaData: MediaHydrated) => {
      const showImage = isImagePreviewSupported(mediaData.extension);
      return (
        <div key={mediaData.id} className="ml-3">
          {mediaData.type !== 'image' || !showImage ? (
            <div className="flex items-center justify-center h-full w-full">
              <div className="flex items-center justify-center rounded-full h-12 w-12 bg-blue-300 dark:text-slate-900">
                {mediaData.extension || ''}
              </div>
            </div>
          ) : (
            <img
              src={getMediaUrl(mediaData, 0, 50)}
              alt={mediaData.fileName}
              className="w-full h-full max-h-[50px] object-center object-contain"
              {...getDefaultImageProperties()}
            />
          )}
        </div>
      );
    },
    sortable: false,
    resizable: true,
    baseWidth: 150,
  },
  name: {
    accessor: 'fileName',
    label: t('Media.Name'),
    width: gridOptionsByID?.['fileName']?.width,
    render: (fileName: string) =>
      defaultRenderer(
        <>
          <ArrowUpRightIcon className="inline-block group-hover:opacity-50 w-3 mr-2" />
          <span className="group-hover:underline">{fileName}</span>
        </>,
      ),
    sortable: true,
    resizable: true,
  },
  dimensions: {
    accessor: 'dimensions',
    label: t('Media.Dimensions'),
    width: gridOptionsByID?.['dimensions']?.width,
    render: (_: undefined, mediaData: MediaHydrated) =>
      defaultRenderer(
        `${mediaData.width || 'N/D'} x ${mediaData.height || 'N/D'} px`,
      ),
    sortable: false,
    resizable: true,
    baseWidth: 150,
  },
  size: {
    accessor: 'size',
    label: t('Media.Size'),
    width: gridOptionsByID?.['size']?.width,
    render: (_: number, mediaData: MediaHydrated) =>
      defaultRenderer(getDataSize(mediaData.size)),
    sortable: true,
    resizable: true,
    baseWidth: 120,
  },
  createdAt: {
    accessor: 'internal.createdAt',
    label: t('Media.UploadDate'),
    width: gridOptionsByID?.['internal.createdAt']?.width,
    render: (createdAt: string) => (
      <DateContainer date={createdAt} isBold={false} />
    ),
    sortable: true,
    resizable: true,
    showByDefault: true,
    baseWidth: 150,
  },
  tags: {
    accessor: 'tags',
    label: t('Media.Tags'),
    width: gridOptionsByID?.['tags']?.width,
    render: (tags: Tag[], mediaData: MediaHydrated) => (
      <div className="w-full truncate">
        {onTagEdit && (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onTagEdit(mediaData);
            }}
            iconImage={<TagIcon className="w-5" />}
            buttonColor="borderless"
            disabled={!canUpdate}
            additionalClasses="inline-block align-middle p-2! hover:opacity-50"
            {...getTestProps(testId, `${mediaData.id}-edit-tags`, 'testId')}
          />
        )}
        {tags
          ?.filter((tag) => tag)
          .map((tag) => (
            <StatusPill
              key={tag.id}
              status={tag.name}
              containerClasses={twMerge(
                'text-xs lg:text-sm lg:py-1.5 lg:px-3 lg:h-7 py-0.5 px-1 h-fit truncate ml-1',
                'bg-slate-200! dark:bg-slate-900! dark:text-slate-400 dark:border-slate-400',
              )}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onTagClick(tag.id, tag.name);
              }}
              {...getTestProps(testId, `${mediaData.id}-${tag.id}`, 'testId')}
            />
          ))}
      </div>
    ),
    sortable: false,
    resizable: true,
  },
  variants: {
    accessor: 'variants',
    label: t('Media.Variants'),
    width: gridOptionsByID?.['variants']?.width,
    render: (variants: Variant[], mediaData: MediaHydrated) => (
      <div className="w-full truncate">
        <Button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onVariantCreate(mediaData);
          }}
          iconImage={<AddVariantIcon className="w-4 text-slate-400" />}
          buttonColor="borderless"
          disabled={!canUpdate}
          additionalClasses="inline-block align-middle p-2! hover:opacity-50"
        />
        {variants?.length || 0}
      </div>
    ),
    sortable: false,
    resizable: true,
    baseWidth: 120,
  },
  extension: {
    accessor: 'extension',
    label: t('Media.Extension'),
    width: gridOptionsByID?.['extension']?.width,
    render: (extension: string) => defaultRenderer(extension),
    sortable: true,
    resizable: true,
    baseWidth: 120,
  },
  type: {
    accessor: 'type',
    label: t('Media.Type'),
    width: gridOptionsByID?.['type']?.width,
    render: (type: string) => defaultRenderer(type),
    sortable: true,
    resizable: true,
    baseWidth: 100,
  },
});
